export interface PublicSubagentExecutionParams {
	action?: unknown;
	agent?: unknown;
	task?: unknown;
	step?: unknown;
	tasks?: unknown;
	chain?: unknown;
	parallel?: unknown;
	concurrency?: unknown;
	chainDir?: unknown;
	workflowScript?: unknown;
	resume?: unknown;
	clarify?: unknown;
}

export type PublicSubagentExecutionMode = "workflow" | "management";

export type PublicSubagentExecutionNormalization<T> =
	| { ok: true; params: T }
	| { ok: false; error: string; mode: PublicSubagentExecutionMode };
/**
 * Enforce the public execution boundary before requests reach the executor.
 * Internal workflow children and structured delegation bypass this boundary.
 *
 * Public execution has one direct mode: `{ agent?, task }`. Omit `agent` to
 * use the built-in `worker`; omit `action` so management and execution remain
 * separate. Scripted workflows remain available as the explicit orchestration
 * escape hatch for callers that need one durable background request.
 */
export function normalizePublicSubagentExecution<T extends PublicSubagentExecutionParams>(
	params: T,
): PublicSubagentExecutionNormalization<T> {
	const action = params.action;
	if (action !== undefined && (typeof action !== "string" || !action.trim())) {
		return {
			ok: false,
			error: "action must be a non-empty management/control action, or omit action for direct execution.",
			mode: "management",
		};
	}
	const normalizedAction = typeof action === "string" ? action.trim() : undefined;
	if (params.clarify !== undefined) {
		return {
			ok: false,
			error: "Public execution does not support clarify UI.",
			mode: "workflow",
		};
	}
	if (params.resume !== undefined) {
		return {
			ok: false,
			error: "Top-level resume execution is not available. Put resume on a workflowScript runs.run/runs.all item.",
			mode: "workflow",
		};
	}
	const hasLegacyOrchestration =
		params.tasks !== undefined ||
		params.chain !== undefined ||
		params.parallel !== undefined ||
		params.concurrency !== undefined ||
		params.chainDir !== undefined;
	if (hasLegacyOrchestration) {
		return {
			ok: false,
			error: "Top-level chain and parallel inputs are not public. Call direct agents from the parent, or use workflowScript.",
			mode: normalizedAction ? "management" : "workflow",
		};
	}
	if (normalizedAction !== undefined) {
		const legacyAction = normalizedAction.toLowerCase();
		if (legacyAction === "single") {
			return {
				ok: false,
				error: "Direct execution omits action; pass { agent?, task } directly.",
				mode: "workflow",
			};
		}
		if (legacyAction === "parallel" || legacyAction === "tasks" || legacyAction === "chain") {
			return {
				ok: false,
				error: "Top-level chain and parallel inputs are not public. Call direct agents from the parent, or use workflowScript.",
				mode: "workflow",
			};
		}
		if (normalizedAction === "schedule.create") {
			if (params.agent !== undefined || params.task !== undefined || params.step !== undefined) {
				return {
					ok: false,
					error: "schedule.create requires workflowScript and does not accept direct agent, task, or step execution fields.",
					mode: "management",
				};
			}
			if (typeof params.workflowScript !== "string" || !params.workflowScript.trim()) {
				return { ok: false, error: "schedule.create requires a non-empty workflowScript.", mode: "management" };
			}
			return { ok: true, params: { ...params, action: normalizedAction } };
		}
		if (params.task !== undefined || params.step !== undefined) {
			return {
				ok: false,
				error: "Management/control actions cannot include direct task or step fields.",
				mode: "management",
			};
		}
		if (params.agent !== undefined && (typeof params.agent !== "string" || !params.agent.trim())) {
			return {
				ok: false,
				error: "Management agent selectors must be non-empty strings.",
				mode: "management",
			};
		}
		if (params.workflowScript !== undefined) {
			return {
				ok: false,
				error: "workflowScript execution must omit action; only schedule.create accepts action with workflowScript.",
				mode: "management",
			};
		}
		return { ok: true, params: { ...params, action: normalizedAction } };
	}
	if (params.step !== undefined) {
		return {
			ok: false,
			error: "step is control-only for action='append-step'; use direct { agent?, task } or workflowScript for execution.",
			mode: "workflow",
		};
	}
	if (params.agent !== undefined || params.task !== undefined) {
		if (params.agent !== undefined && (typeof params.agent !== "string" || !params.agent.trim())) {
			return { ok: false, error: "Direct agent must be a non-empty string.", mode: "workflow" };
		}
		if (typeof params.task !== "string" || !params.task.trim()) {
			return { ok: false, error: "Direct execution requires a non-empty task.", mode: "workflow" };
		}
		if (params.workflowScript !== undefined) {
			return {
				ok: false,
				error: "Direct execution and workflowScript cannot be combined.",
				mode: "workflow",
			};
		}
		return { ok: true, params };
	}
	if (typeof params.workflowScript !== "string" || !params.workflowScript.trim()) {
		return {
			ok: false,
			error: "Execution requires either { agent?, task } or a non-empty workflowScript.",
			mode: "workflow",
		};
	}
	return { ok: true, params };
}
